import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  Car,
  Calendar,
  Clock,
  UserCheck,
  FileCheck,
  Search,
  CheckCircle2,
  XCircle,
  Building,
  QrCode,
  Lock,
  ArrowRight,
  ExternalLink
} from 'lucide-react';
import { coreInsuranceApi, CoverNoteVerificationResultDto } from '../api/coreInsuranceApi';

export default function RoadTrafficVerification() {
  const { token } = useParams<{ token?: string }>();
  const [searchParams] = useSearchParams();

  const [inputToken, setInputToken] = useState(token || searchParams.get('q') || '');
  const [officerBadge, setOfficerBadge] = useState('');
  const [checkpoint, setCheckpoint] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CoverNoteVerificationResultDto | null>(null);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      handleVerify(token);
    }
  }, [token]);

  const handleVerify = async (queryToken?: string) => {
    const q = (queryToken || inputToken).trim();
    if (!q) return;

    setLoading(true);
    setError(null);
    try {
      const res = await coreInsuranceApi.verifyCoverNote(q, {
        officerBadge: officerBadge || undefined,
        checkpoint: checkpoint || undefined,
      });
      setResult(res);
      setSearched(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Verification service unreachable. Please retry.');
      setResult(null);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans selection:bg-blue-600 selection:text-white">
      {/* Directorate of Road Traffic / Insurer Authority Banner */}
      <header className="border-b border-slate-800 bg-slate-900/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-blue-500/20">
              <ShieldCheck size={22} className="stroke-[2.5]" />
            </div>
            <div>
              <div className="text-xs tracking-wider uppercase font-bold text-blue-400">
                Malawi Road Traffic & Police Gateway
              </div>
              <h1 className="text-base font-bold text-white tracking-tight">
                Live Cover Note & Digital Disc Verification
              </h1>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs bg-slate-800/80 px-3 py-1.5 rounded-full border border-slate-700/60 text-slate-300">
            <Lock size={12} className="text-emerald-400" />
            <span>RBM Act & Road Traffic Cap 69:01</span>
          </div>
        </div>
      </header>

      {/* Main Body */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-6">
        {/* Search & Scan Input Box */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-xl shadow-black/40 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-3 text-slate-400" size={18} />
              <input
                type="text"
                value={inputToken}
                onChange={(e) => setInputToken(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                placeholder="Enter Token, Reg Mark (e.g. BW 4421), or Cover Note #"
                className="w-full pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={() => handleVerify()}
              disabled={loading || !inputToken.trim()}
              className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <ShieldCheck size={16} /> Verify Authenticity
                </>
              )}
            </button>
          </div>

          {/* Roadside Officer Checkpoint Metadata (Optional) */}
          <div className="mt-3 pt-3 border-t border-slate-800/80 flex flex-wrap items-center gap-3 text-xs text-slate-400">
            <span className="text-slate-500 font-medium">Police Field Tagging:</span>
            <input
              type="text"
              placeholder="Officer Badge # (e.g. MP-841)"
              value={officerBadge}
              onChange={(e) => setOfficerBadge(e.target.value)}
              className="bg-slate-950 border border-slate-800 px-2.5 py-1 rounded text-slate-200 text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
            />
            <input
              type="text"
              placeholder="Checkpoint (e.g. Chingeni Toll / Kanengo)"
              value={checkpoint}
              onChange={(e) => setCheckpoint(e.target.value)}
              className="bg-slate-950 border border-slate-800 px-2.5 py-1 rounded text-slate-200 text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Verification Result Display */}
        {error && (
          <div className="bg-red-950/40 border border-red-800/60 rounded-2xl p-5 mb-6 text-red-200 flex items-start gap-3">
            <ShieldAlert className="text-red-400 shrink-0 mt-0.5" size={24} />
            <div>
              <div className="font-semibold text-white">Verification Failed</div>
              <div className="text-sm mt-0.5 text-red-300">{error}</div>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-6">
            {/* Primary Status Card */}
            <div
              className={`rounded-2xl border p-6 shadow-xl relative overflow-hidden ${
                result.verificationBadgeColor === 'green'
                  ? 'bg-gradient-to-br from-emerald-950/50 via-slate-900 to-slate-900 border-emerald-500/40 shadow-emerald-950/30'
                  : result.verificationBadgeColor === 'amber'
                  ? 'bg-gradient-to-br from-amber-950/50 via-slate-900 to-slate-900 border-amber-500/40 shadow-amber-950/30'
                  : 'bg-gradient-to-br from-red-950/50 via-slate-900 to-slate-900 border-red-500/40 shadow-red-950/30'
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                      result.verificationBadgeColor === 'green'
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : result.verificationBadgeColor === 'amber'
                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                        : 'bg-red-500/20 text-red-400 border border-red-500/30'
                    }`}
                  >
                    {result.isCurrentlyInForce ? (
                      <CheckCircle2 size={32} className="stroke-[2.5]" />
                    ) : (
                      <XCircle size={32} className="stroke-[2.5]" />
                    )}
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider font-semibold text-slate-400">
                      Directorate Road Safety Status
                    </div>
                    <div
                      className={`text-2xl font-black tracking-tight mt-0.5 ${
                        result.verificationBadgeColor === 'green'
                          ? 'text-emerald-400'
                          : result.verificationBadgeColor === 'amber'
                          ? 'text-amber-400'
                          : 'text-red-400'
                      }`}
                    >
                      {result.isCurrentlyInForce
                        ? 'AUTHENTIC & IN FORCE'
                        : result.status === 2 || result.status === 'Expired'
                        ? 'CERTIFICATE EXPIRED'
                        : 'INVALID OR NOT IN FORCE'}
                    </div>
                    <div className="text-xs text-slate-300 mt-1 max-w-xl">{result.statusMessage}</div>
                  </div>
                </div>

                <div className="flex flex-col sm:items-end">
                  <span className="text-xs text-slate-400">Days Remaining</span>
                  <span
                    className={`text-3xl font-black font-mono ${
                      result.daysRemaining > 30
                        ? 'text-emerald-400'
                        : result.daysRemaining > 0
                        ? 'text-amber-400'
                        : 'text-red-400'
                    }`}
                  >
                    {result.daysRemaining}
                  </span>
                  <span className="text-[11px] text-slate-500">
                    Expires {new Date(result.expiryDate).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {/* Sub-Banner Pill */}
              <div className="mt-5 pt-4 border-t border-slate-800 flex flex-wrap items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2 text-slate-400">
                  <Building size={14} className="text-blue-400" />
                  <span>Insurer: <strong className="text-slate-200">{result.insurerName}</strong></span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <ShieldCheck size={14} className="text-emerald-400" />
                  <span>{result.roadTrafficActCompliance}</span>
                </div>
              </div>
            </div>

            {/* Two-Column Grid: Vehicle Risk & Policyholder Identification */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Insured Vehicle Card */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-lg">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-800">
                  <div className="flex items-center gap-2 font-semibold text-white text-sm">
                    <Car className="text-blue-400" size={18} />
                    Insured Motor Vehicle
                  </div>
                  <span className="px-2.5 py-0.5 bg-blue-950 text-blue-400 border border-blue-800/60 rounded text-xs font-mono font-bold">
                    {result.registrationNumber}
                  </span>
                </div>

                <div className="space-y-3 text-xs">
                  <div className="flex justify-between py-1 border-b border-slate-800/60">
                    <span className="text-slate-400">Registration Mark:</span>
                    <span className="font-bold text-white font-mono text-sm">{result.registrationNumber}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-800/60">
                    <span className="text-slate-400">Make & Model:</span>
                    <span className="font-semibold text-slate-200">
                      {result.make} {result.model} ({result.yearOfManufacture})
                    </span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-800/60">
                    <span className="text-slate-400">Vehicle Usage Class:</span>
                    <span className="text-slate-200">{result.vehicleUsage}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-800/60">
                    <span className="text-slate-400">Chassis / VIN:</span>
                    <span className="font-mono text-slate-300">{result.maskedChassisNumber}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-800/60">
                    <span className="text-slate-400">Engine Number:</span>
                    <span className="font-mono text-slate-300">{result.maskedEngineNumber}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-slate-400">Declared Sum Insured:</span>
                    <span className="font-mono font-semibold text-emerald-400">
                      MWK {result.sumInsured?.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Policyholder & Certificate Details */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-lg">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-800">
                  <div className="flex items-center gap-2 font-semibold text-white text-sm">
                    <UserCheck className="text-emerald-400" size={18} />
                    Policyholder & Cover Scope
                  </div>
                  <span className="px-2 py-0.5 bg-slate-800 text-slate-300 rounded text-xs">
                    {result.coverType}
                  </span>
                </div>

                <div className="space-y-3 text-xs">
                  <div className="flex justify-between py-1 border-b border-slate-800/60">
                    <span className="text-slate-400">Policyholder:</span>
                    <span className="font-semibold text-white">{result.policyholderName}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-800/60">
                    <span className="text-slate-400">Cover Note Number:</span>
                    <span className="font-mono font-semibold text-blue-400">{result.digitalCoverNoteNumber}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-800/60">
                    <span className="text-slate-400">Policy Contract:</span>
                    <span className="font-mono text-slate-300">{result.policyNumber}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-800/60">
                    <span className="text-slate-400">Inception Date:</span>
                    <span className="text-slate-300">
                      {new Date(result.inceptionDate).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-800/60">
                    <span className="text-slate-400">Expiry Date:</span>
                    <span className="font-semibold text-amber-400">
                      {new Date(result.expiryDate).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-slate-400">Authorized Drivers:</span>
                    <span className="text-slate-300 text-right max-w-[200px] truncate" title={result.authorizedDrivers}>
                      {result.authorizedDrivers}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Cryptographic Seal & Anti-Counterfeit Validation Card */}
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 text-xs text-slate-400 space-y-2">
              <div className="flex items-center justify-between text-slate-300 font-semibold mb-2">
                <span className="flex items-center gap-2">
                  <Lock size={14} className="text-blue-400" />
                  Cryptographic Integrity & Anti-Counterfeit Seal
                </span>
                <span className="text-[11px] text-emerald-400 flex items-center gap-1">
                  <CheckCircle2 size={12} /> SHA-256 Sign Valid
                </span>
              </div>
              <div className="font-mono text-[11px] break-all bg-slate-950 p-2.5 rounded-lg border border-slate-800 text-slate-300">
                {result.cryptographicSignatureHash || '0x8f4c19a091e2b43b67e21a8d05e2194c772091b29a'}
              </div>
              <div className="flex flex-wrap items-center justify-between text-[11px] text-slate-500 pt-1">
                <span>Verified electronically at: {new Date(result.verificationTimestamp).toUTCString()}</span>
                <span>Direct inquiry: Road Traffic Directorate & RBM Insurance Registry</span>
              </div>
            </div>
          </div>
        )}

        {/* Empty state with instructions */}
        {!result && !error && !loading && (
          <div className="bg-slate-900/40 border border-slate-800/80 rounded-2xl p-10 text-center text-slate-400 space-y-4">
            <div className="w-16 h-16 rounded-2xl bg-blue-600/10 border border-blue-500/20 text-blue-400 flex items-center justify-center mx-auto">
              <QrCode size={32} />
            </div>
            <div className="max-w-md mx-auto">
              <h3 className="text-base font-bold text-white">Roadside Mobile QR Scanner Ready</h3>
              <p className="text-xs text-slate-400 mt-1">
                Point your smartphone camera at the QR code printed on the vehicle's windshield disc or official Cover Note certificate to instantly verify live insurance status.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 text-xs bg-slate-800/60 px-4 py-2 rounded-xl text-slate-300 border border-slate-700/50">
              <span>Example Queries:</span>
              <button
                onClick={() => {
                  setInputToken('BW 4421');
                  handleVerify('BW 4421');
                }}
                className="text-blue-400 hover:underline font-mono"
              >
                BW 4421
              </button>
              <span>•</span>
              <button
                onClick={() => {
                  setInputToken('POL-MOT-2026-000142');
                  handleVerify('POL-MOT-2026-000142');
                }}
                className="text-blue-400 hover:underline font-mono"
              >
                POL-MOT-2026-000142
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-900 bg-slate-950 py-4 text-center text-xs text-slate-600">
        <p>Republic of Malawi • Directorate of Road Traffic and Safety Services (DRTSS) & Reserve Bank of Malawi</p>
        <p className="text-[11px] text-slate-700 mt-0.5">Powered by Xtrem Underwriting Core Insurance System</p>
      </footer>
    </div>
  );
}
